import React, { useEffect, useState } from "react";
import { useApi } from "../hooks/useApi";
import { formatUtcDateTime } from "../utils/dateTime";

/**
 * Step 6: how much of the population the identity evidence covers, whether the sensors are
 * reporting, and how confident Axaris is about the identity assurance controls. Coverage and
 * health change confidence and the stale flag — never a control's status.
 */
const TONE = { High: "bg-green-50 text-green-700 border-green-200", Medium: "bg-amber-50 text-amber-700 border-amber-200", Low: "bg-red-50 text-red-700 border-red-200" };

export const sensorLine = (s) => {
  if (!s) return "Sensor health: not read yet.";
  switch (s.state) {
    case "consented": return s.unhealthy > 0 || s.openIssues > 0
      ? `Sensor health: ${s.healthy} healthy · ${s.unhealthy} unhealthy · ${s.openIssues} open issue${s.openIssues === 1 ? "" : "s"}`
      : `Sensor health: ${s.healthy} sensor${s.healthy === 1 ? "" : "s"} healthy`;
    case "noSensors": return "No Defender for Identity sensors are reporting — the identity evidence is Entra ID only.";
    case "notConsented": return "Sensor health: not consented — grant SecurityIdentitiesSensors.Read.All and SecurityIdentitiesHealth.Read.All to the Axaris app and re-consent.";
    case "failed": return `Sensor health: read failed — ${s.message}`;
    default: return "Sensor health: not read yet.";
  }
};

export default function IdentityCoverageCard({ organizationId }) {
  const apiCall = useApi();
  const [cov, setCov] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiCall("GET", "/defender-identity/coverage")
      .then(c => { if (!cancelled) { if (c && typeof c.coveragePercent === "number") setCov(c); else setError(true); } })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [apiCall, organizationId]);

  if (error) return null;
  if (!cov) return <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 text-xs text-gray-400" data-testid="identity-coverage">Reading evidence coverage…</div>;

  const age = cov.evidenceAgeHours == null ? "never synced" : cov.evidenceAgeHours < 1 ? "synced within the hour" : cov.evidenceAgeHours < 48 ? `synced ${Math.round(cov.evidenceAgeHours)} h ago` : `synced ${Math.round(cov.evidenceAgeHours / 24)} d ago`;
  return (
    <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 text-xs" data-testid="identity-coverage">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="font-semibold text-gray-800">Evidence coverage</span>
        <span className="text-gray-700"><span className="font-semibold tabular-nums">{cov.coveragePercent}%</span> of {cov.enabledIdentities.toLocaleString()} enabled user accounts covered by Entra ID</span>
        <span className="text-gray-500">{age}{cov.lastSuccessfulSyncAt ? ` (${formatUtcDateTime(cov.lastSuccessfulSyncAt)})` : ""}</span>
        <span className={cov.signInActivityAvailable ? "text-green-700" : cov.mdiActivityAvailable ? "text-green-700" : "text-amber-700"}>
          {cov.signInActivityAvailable ? "Sign-in activity available" : cov.mdiActivityAvailable ? "MDI activity available" : "No sign-in or MDI activity — dormancy controls not assessed"}
        </span>
        {cov.lastPullTruncated && <span className="text-amber-700">Last pull truncated</span>}
        <span className="ml-auto flex items-center gap-1.5" title={cov.confidenceModel?.sourceLabel || ""}>
          <span className="text-gray-500">Confidence</span>
          <span className={`px-1.5 py-0.5 rounded border font-semibold ${TONE[cov.confidenceLabel] || TONE.Medium}`} data-testid="identity-confidence">{cov.confidenceLabel} · {Math.round(cov.confidenceScore * 100)}%</span>
        </span>
      </div>
      <div className="mt-1 text-gray-500 flex flex-wrap gap-x-4 gap-y-0.5">
        <span>{sensorLine(cov.sensors)}</span>
        <span>Devices: {cov.mdeCovered} of {cov.devices} seen by Defender for Endpoint{cov.deviceInfoAvailable ? " · DeviceInfo available" : " · no DeviceInfo table"}</span>
      </div>
      {cov.confidenceModel && (
        <details className="mt-1">
          <summary className="text-gray-500 cursor-pointer">How the confidence is computed</summary>
          <div className="text-gray-600 mt-1">
            Source reliability {cov.confidenceModel.sourceReliability} · freshness +{cov.confidenceModel.dataFreshnessBonus} · completeness {cov.confidenceModel.completenessPenalty} → <span className="font-semibold">{cov.confidenceModel.finalScore}</span>
            <div className="text-gray-400">{cov.confidenceModel.sourceLabel}</div>
            <div className="text-gray-400">Coverage and health change confidence only — never a control's status. Missing evidence makes a control Not Assessed, never failed.</div>
          </div>
        </details>
      )}
    </div>
  );
}
